import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { BadgeCheckIcon, CalendarCheckIcon, MapIcon, UtensilsIcon } from 'lucide-react';
import { Card, SectionHeading } from '../../components/ui/Panels';
import { ButtonLink } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/Field';
import { IMG } from '../../data/images';
import { currency } from '../../utils/format';
import { SERVICE_FEE_RATE } from '../../utils/pricing';
import { spring, viewportOnce } from '../../utils/motion';
const controls = [{
  icon: <UtensilsIcon className="h-5 w-5" aria-hidden="true" data-id="element-2129" data-mp="3itmrbnv33dlb" />,
  title: 'Your menus, your prices',
  body: 'Set a price per guest, a minimum head count, a minimum order, and the hours included. Add-ons stay separate and visible.'
}, {
  icon: <MapIcon className="h-5 w-5" aria-hidden="true" data-id="element-2130" data-mp="3rv2t577u27dx" />,
  title: 'Your service areas',
  body: 'Draw where you will cook, where you will deliver, and how far you travel before per-kilometre charges apply.'
}, {
  icon: <CalendarCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2131" data-mp="20x7wv4kefkyo" />,
  title: 'Your calendar',
  body: 'Request-to-book by default. Turn on instant book only for the dates you are certain about.'
}, {
  icon: <BadgeCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2132" data-mp="1rwjiu9v09kls" />,
  title: 'Your credentials, shown',
  body: 'ID, food hygiene, insurance, and an optional background check appear as badges guests can filter on.'
}];
export function BecomeChef() {
  const reduce = useReducedMotion();
  const [pricePerGuest, setPricePerGuest] = useState(1400);
  const [guests, setGuests] = useState(6);
  const [bookingsPerMonth, setBookingsPerMonth] = useState(6);
  const perBooking = pricePerGuest * guests;
  const gross = perBooking * bookingsPerMonth;
  const fee = Math.round(gross * SERVICE_FEE_RATE);
  const net = gross - fee;
  return <>
      <section className="border-b border-hairline" data-id="element-2133" data-mp="33cqeixc3yqbe">
        <div className="mx-auto grid max-w-shell items-center gap-12 px-5 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-20" data-id="element-2134" data-mp="2yivwrzg2cley">
          <div data-id="element-2135" data-mp="wn6k9ea25op9">
            <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-action" data-id="element-2136" data-mp="wohkj44aoebm">Cook with Pan &amp; Apron</p>
            <h1 className="mt-4 max-w-[24ch] font-display text-[clamp(2.1rem,4.2vw,3.6rem)] font-semibold leading-[1.04] tracking-[-0.03em] text-ink" data-id="element-2137" data-mp="mwtpkt8j4f5e">
              Get paid for the menu, not for the hour.
            </h1>
            <p className="mt-5 max-w-[54ch] text-lg leading-relaxed text-ink-2" data-id="element-2138" data-mp="cy4gnpym251a">
              Publish the food you are actually best at, quote from it in two taps, and get a deposit before you shop. You keep
              90% of every line on the quote.
            </p>
            <div className="mt-7 flex flex-wrap gap-3" data-id="element-2139" data-mp="8wsrfljnfltm">
              <ButtonLink to="/chef/onboarding" variant="action" size="lg" data-id="element-2140" data-mp="8xc6muhwzw3y">
                Start a listing
              </ButtonLink>
              <ButtonLink to="/chef" variant="outline" size="lg" data-id="element-2141" data-mp="23atmrwfzpqoy">
                See the chef dashboard
              </ButtonLink>
            </div>
          </div>
          <motion.div initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={reduce ? {
          duration: 0.2
        } : spring.heavy} className="overflow-hidden rounded-2xl border border-hairline" data-id="element-2142" data-mp="2df9zigy6aroj">
            <img src={IMG.chefAnan} alt="A private chef at his prep counter" className="aspect-[4/3] w-full object-cover" data-id="element-2143" data-mp="2hesp3q0xvqxp" />
          </motion.div>
        </div>
      </section>

      <section aria-labelledby="calc-heading" className="border-b border-hairline bg-oat" data-id="element-2144" data-mp="2r53rh84z5s2h">
        <div className="mx-auto grid max-w-shell gap-10 px-5 py-16 lg:grid-cols-[1fr_1fr] lg:px-8 lg:py-20" data-id="element-2145" data-mp="3567trpg6ukc4">
          <div data-id="element-2146" data-mp="315e68x6nr7jr">
            <h2 id="calc-heading" className="font-display text-3xl font-semibold tracking-[-0.02em] text-ink" data-id="element-2147" data-mp="314ukt6hsu10z">
              What a month looks like
            </h2>
            <p className="mt-3 max-w-[52ch] text-sm leading-relaxed text-ink-2" data-id="element-2148" data-mp="16r3gmmi8o9cx">
              Move the three numbers that actually matter. This is menu revenue only — add-ons, wine pairings, and travel are
              on top, and groceries are either included or reimbursed.
            </p>
            <div className="mt-6 space-y-5" data-id="element-2149" data-mp="1h7nlcyqc6cf1">
              <div data-id="element-2150" data-mp="1h8ykgl8u6vzt">
                <label htmlFor="calc-price" className="flex items-baseline justify-between gap-3 text-sm" data-id="element-2151" data-mp="1h6chkyww54nx">
                  <span className="font-medium text-ink" data-id="element-2152" data-mp="pgzkzowhlb2f">Price per guest</span>
                  <span className="tnum font-display text-lg font-semibold text-ink" data-id="element-2153" data-mp="pewkuc1me1jz">{currency(pricePerGuest)}</span>
                </label>
                <input id="calc-price" type="range" min={400} max={6000} step={50} value={pricePerGuest} onChange={e => setPricePerGuest(Number(e.target.value))} className="mt-2 w-full accent-primary" data-id="element-2154" data-mp="yh5rurxojcsm" />
                <p className="mt-1 text-[12px] text-ink-3" data-id="element-2155" data-mp="apql8myve9bk">
                  Home-style Thai tables sit near ฿1,000. Tasting menus and omakase run ฿2,400–5,500.
                </p>
              </div>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-2156" data-mp="3ud2bv2yecsvo">
                <div data-id="element-2157" data-mp="3peh84tjxrr2h">
                  <label htmlFor="calc-guests" className="flex items-baseline justify-between gap-3 text-sm" data-id="element-2158" data-mp="3pdz7lkq8qqor">
                    <span className="font-medium text-ink" data-id="element-2159" data-mp="3lgjjbru2v6zm">Guests per booking</span>
                    <span className="tnum font-display text-lg font-semibold text-ink" data-id="element-2160" data-mp="3ggf21zlci88c">{guests}</span>
                  </label>
                  <input id="calc-guests" type="range" min={2} max={24} step={1} value={guests} onChange={e => setGuests(Number(e.target.value))} className="mt-2 w-full accent-primary" data-id="element-2161" data-mp="36pao937eotf3" />
                </div>
                <div data-id="element-2162" data-mp="2srvtdiydvtfq">
                  <label htmlFor="calc-bookings" className="flex items-baseline justify-between gap-3 text-sm" data-id="element-2163" data-mp="2j25mtwc27h5y">
                    <span className="font-medium text-ink" data-id="element-2164" data-mp="2n3r928ahh7st">Bookings per month</span>
                    <span className="tnum font-display text-lg font-semibold text-ink" data-id="element-2165" data-mp="293zo1nljur5b">{bookingsPerMonth}</span>
                  </label>
                  <input id="calc-bookings" type="range" min={1} max={20} step={1} value={bookingsPerMonth} onChange={e => setBookingsPerMonth(Number(e.target.value))} className="mt-2 w-full accent-primary" data-id="element-2166" data-mp="2e3m21z97cbf6" />
                </div>
              </div>
            </div>
          </div>

          <Card className="flex flex-col justify-center p-6 lg:p-8" data-id="element-2167" data-mp="3krj6929jk90e">
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2168" data-mp="3kp6iu9l0l6fy">Your take-home, per month</p>
            <p className="tnum mt-2 font-display text-[clamp(2.4rem,5vw,3.6rem)] font-semibold leading-none tracking-[-0.03em] text-primary" data-id="element-2169" data-mp="1e2qym4j41use">
              {currency(net)}
            </p>
            <dl className="mt-6 space-y-2 border-t border-hairline pt-4 text-sm" data-id="element-2170" data-mp="192361ef8h5w2">
              <div className="flex justify-between" data-id="element-2171" data-mp="1mxf1xp54l2ek">
                <dt className="text-ink-2" data-id="element-2172" data-mp="1ith6bof0e9q9">Per booking</dt>
                <dd className="tnum font-medium text-ink" data-id="element-2173" data-mp="1iw3d3lu2sl7f">
                  {currency(perBooking)} <span className="font-normal text-ink-3" data-id="element-2174" data-mp="v68z30y0sqll">· {guests} guests</span>
                </dd>
              </div>
              <div className="flex justify-between" data-id="element-2175" data-mp="144tcpnoq4a2y">
                <dt className="text-ink-2" data-id="element-2176" data-mp="146e6hb37rozr">Menu revenue</dt>
                <dd className="tnum font-medium text-ink" data-id="element-2177" data-mp="z58cl9xthvon">{currency(gross)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-2178" data-mp="2fjgmo51eicje">
                <dt className="text-ink-2" data-id="element-2179" data-mp="2fi59jem2fsfm">Pan &amp; Apron service fee (10%)</dt>
                <dd className="tnum font-medium text-ink" data-id="element-2180" data-mp="2ak3s0y4ag169">−{currency(fee)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-2181" data-mp="2uoawjcjnd328">
                <dt className="text-ink-2" data-id="element-2182" data-mp="2zluo1e5cvcc4">Payouts</dt>
                <dd className="font-medium text-ink" data-id="element-2183" data-mp="2zmmc83wmi7sd">Twice monthly</dd>
              </div>
            </dl>
            <p className="mt-4 text-[12px] text-ink-3" data-id="element-2184" data-mp="33memcl1o0lzu">
              Illustrative. Taxes are yours to handle; we generate the annual summary.
            </p>
          </Card>
        </div>
      </section>

      <section aria-labelledby="controls-heading" className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-2185" data-mp="3dflnftpdhfu4">
        <SectionHeading level={2} title="What stays under your control" data-id="element-2186" data-mp="3ief23pgebcvv" />
        <motion.ul initial="hidden" whileInView="show" viewport={viewportOnce} variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.06
          }
        }
      }} className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2187" data-mp="3nc6wcq4ijb38">
          {controls.map(item => <motion.li key={item.title} variants={reduce ? {
          hidden: {
            opacity: 0
          },
          show: {
            opacity: 1,
            transition: {
              duration: 0.15
            }
          }
        } : {
          hidden: {
            opacity: 0,
            y: 18
          },
          show: {
            opacity: 1,
            y: 0,
            transition: spring.smooth
          }
        }} data-id="element-2188" data-mp="1x1byoyh7xp2r">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-soft text-primary" data-id="element-2189" data-mp="1bov7u6ecib9q">
                {item.icon}
              </span>
              <h3 className="mt-4 font-display text-lg font-semibold text-ink" data-id="element-2190" data-mp="28c89m3ffiyb5">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-2" data-id="element-2191" data-mp="23doo244qzojb">{item.body}</p>
            </motion.li>)}
        </motion.ul>
      </section>
    </>;
}